import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config/api';

const money = (v) => (Number(v) || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽';

const ruCount = (n, one, few, many) => {
    const abs = Math.abs(Number(n) || 0) % 100;
    const d = abs % 10;
    if (abs > 10 && abs < 20) return `${n} ${many}`;
    if (d === 1) return `${n} ${one}`;
    if (d >= 2 && d <= 4) return `${n} ${few}`;
    return `${n} ${many}`;
};

const guideCaption = (r) => {
    const parts = [];
    if (r.shifts) parts.push(ruCount(r.shifts, 'выход', 'выхода', 'выходов'));
    if (r.sold_reimbursed) {
        parts.push(r.sold_reimbursed === 1 ? 'продажа 1 смены' : `продано ${r.sold_reimbursed} смен`);
    }
    if (r.extra_shifts) parts.push(ruCount(r.extra_shifts, 'доп. смена', 'доп. смены', 'доп. смен'));
    if (r.extra_1) parts.push(r.extra_1 === 1 ? '+1 экскурсия' : `+1 экскурсия × ${r.extra_1}`);
    if (r.extra_2) parts.push(r.extra_2 === 1 ? '+2 экскурсии' : `+2 экскурсии × ${r.extra_2}`);
    return parts.join(' · ');
};

const dayTitle = (s) => {
    const bits = [fmtDay(s.date), s.type];
    if (s.start || s.end) bits.push(`${s.start || '—'}${s.end ? `–${s.end}` : ''}`);
    if (s.excursions) bits.push(ruCount(s.excursions, 'экскурсия', 'экскурсии', 'экскурсий'));
    return bits.join(' · ');
};

const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const WEEKDAYS = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];

const fmtDay = (iso) => {
    if (!iso) return '—';
    const [y, m, d] = String(iso).slice(0, 10).split('-');
    return `${d}.${m}.${y}`;
};

const fmtDayTitle = (iso) => {
    if (!iso) return '—';
    const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return `${d} ${MONTHS[m - 1]}, ${WEEKDAYS[dt.getDay()]}`;
};

const groupByDate = (rows) => {
    const groups = [];
    (rows || []).forEach((row) => {
        const date = String(row.date || '').slice(0, 10);
        const last = groups[groups.length - 1];
        if (!last || last.date !== date) groups.push({ date, items: [row] });
        else last.items.push(row);
    });
    return groups;
};

const extrasPay = (row) => (Number(row.bonuses) || 0) + (Number(row.cafe_pay) || 0);

const applyGuideRate = (payload, rate) => {
    const n = Number(rate) || 0;
    const guides = (payload.guides || []).map((row) => {
        const shiftPay = (row.paid_shifts || 0) * n;
        const extras = extrasPay(row);
        return {
            ...row,
            shift_rate: n,
            shift_pay: shiftPay,
            extras_pay: extras,
            total: extras,
        };
    });
    return {
        ...payload,
        rates: { ...payload.rates, guide_shift_pay: n },
        guides,
        totals: {
            ...payload.totals,
            guides: guides.reduce((sum, row) => sum + extrasPay(row), 0),
        },
    };
};

const toIsoDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

export default function PayrollPanel({ user, token }) {
    const today = new Date();
    const firstDay = toIsoDate(new Date(today.getFullYear(), today.getMonth(), 1));
    const lastDay = toIsoDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
    const [startDate, setStartDate] = useState(firstDay);
    const [endDate, setEndDate] = useState(lastDay);
    const [tab, setTab] = useState('guides');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const canEdit = ['owner', 'admin'].includes(user?.role);
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            await fetch(`${API_URL}/api/work-sessions/recalculate`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ date_from: startDate, date_to: endDate }),
            });
            const res = await fetch(
                `${API_URL}/api/payroll/from-calendar?period_start=${startDate}&period_end=${endDate}`,
                { headers }
            );
            const json = await res.json();
            if (!json.success) throw new Error(json.error || 'Ошибка расчёта');
            setData(json);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, token]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const res = await fetch(`${API_URL}/api/payroll/from-calendar`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ period_start: startDate, period_end: endDate }),
            });
            const json = await res.json();
            if (!json.success) throw new Error(json.error || 'Не удалось сохранить');
            setSuccess(`Ведомость сохранена: ${json.saved} сотрудников`);
            setData(json);
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const exportCsv = () => {
        if (!data) return;
        const rows = tab === 'guides'
                    ? [['Гид', 'Выходы', 'Фикс', 'Допы'],
                ...(data.guides || []).map((r) => [r.employee_name, r.shifts, r.shift_pay, extrasPay(r)])]
            : [['Админ', 'Количество смен', 'Количество барных смен', 'Количество основных смен', '7% от сувенирки'],
                ...(data.admins || []).map((r) => [r.employee_name, r.shifts, r.bar_shifts, r.main_shifts ?? ((r.shifts || 0) - (r.bar_shifts || 0)), r.souvenirs_pay])];
        const csv = rows.map((r) => r.join(';')).join('\n');
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `zp_${tab}_${startDate}_${endDate}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const openDetail = async (employeeId, kind) => {
        setDetailLoading(true);
        setDetail({ loading: true });
        try {
            const res = await fetch(
                `${API_URL}/api/payroll/from-calendar/detail?employee_id=${employeeId}&period_start=${startDate}&period_end=${endDate}&kind=${kind}`,
                { headers }
            );
            const json = await res.json();
            if (!json.success) throw new Error(json.error || 'Не удалось открыть расшифровку');
            setDetail(json);
        } catch (e) {
            setError(e.message);
            setDetail(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const guides = data?.guides || [];
    const admins = data?.admins || [];
    const cafePct = data?.rates?.cafe_percent ?? 10;
    const souvPct = data?.rates?.souvenirs_percent ?? 7;
    const guideRate = data?.rates?.guide_shift_pay ?? 0;

    const saveGuideRate = async (value) => {
        if (!canEdit) return;
        const n = Number(value) || 0;
        setData((d) => (d ? applyGuideRate(d, n) : d));
        try {
            const res = await fetch(`${API_URL}/api/payroll/rates`, {
                method: 'PUT',
                headers,
                body: JSON.stringify({ guide_shift_pay: n }),
            });
            const json = await res.json();
            if (!json.success) throw new Error(json.error || 'Не удалось сохранить ставку');
        } catch (e) {
            setError(e.message);
        }
    };

    const recalc = async () => {
        const currentRate = data?.rates?.guide_shift_pay;
        if (canEdit && currentRate !== undefined && currentRate !== '') {
            await saveGuideRate(currentRate);
        }
        await load();
    };

    return (
        <Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Расчёт зарплаты</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Два платежа: фикс (факт и замена из календаря, даже без времени) и допы (доп. смены, экскурсии, кафе).
                        Нажмите на гида, чтобы увидеть дни. Купленная смена покупателю в ЗП не входит.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!data}>CSV</Button>
                    {canEdit && (
                        <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving || loading}>
                            Сохранить ведомость
                        </Button>
                    )}
                </Stack>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                    <TextField type="date" label="С" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                    <TextField type="date" label="По" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                    <TextField
                        type="number"
                        label="Ставка факта / замены"
                        size="small"
                        value={guideRate}
                        onChange={(e) => {
                            const n = e.target.value;
                            setData((d) => (d ? applyGuideRate(d, n) : d));
                        }}
                        onBlur={(e) => saveGuideRate(e.target.value)}
                        InputProps={{ endAdornment: <Typography sx={{ ml: 0.5 }}>₽</Typography> }}
                        sx={{ maxWidth: 220 }}
                    />
                    <Button variant="outlined" onClick={recalc} disabled={loading}>{loading ? 'Считаем…' : 'Пересчитать'}</Button>
                </Stack>
            </Paper>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab value="guides" label="Гиды" />
                <Tab value="admins" label="Админы" />
            </Tabs>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : tab === 'guides' ? (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Гид</TableCell>
                                <TableCell align="right">Фикс</TableCell>
                                <TableCell align="right">Допы</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {guides.length === 0 && (
                                <TableRow><TableCell colSpan={3} align="center">Нет смен в календаре за этот период</TableCell></TableRow>
                            )}
                            {guides.map((r) => (
                                <TableRow
                                    key={r.employee_id}
                                    hover
                                    onClick={() => openDetail(r.employee_id, 'guide')}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>
                                        <Typography>{r.employee_name}</Typography>
                                        {guideCaption(r) ? (
                                            <Typography variant="caption" color="text.secondary">{guideCaption(r)}</Typography>
                                        ) : null}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography sx={{ fontWeight: 700 }}>{money(r.shift_pay)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography sx={{ fontWeight: 700 }}>{money(extrasPay(r))}</Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                        {guides.length > 0 && (
                            <TableFooter>
                                <TableRow>
                                    <TableCell><b>Итого</b></TableCell>
                                    <TableCell align="right"><b>{money(guides.reduce((s, r) => s + (r.shift_pay || 0), 0))}</b></TableCell>
                                    <TableCell align="right"><b>{money(guides.reduce((s, r) => s + extrasPay(r), 0))}</b></TableCell>
                                </TableRow>
                            </TableFooter>
                        )}
                    </Table>
                </TableContainer>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Администратор</TableCell>
                                <TableCell align="right">Смены</TableCell>
                                <TableCell align="right">Барные</TableCell>
                                <TableCell align="right">Основные</TableCell>
                                <TableCell align="right">{souvPct}% сувениры</TableCell>
                                <TableCell align="right">Итого</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {admins.length === 0 && (
                                <TableRow><TableCell colSpan={6} align="center">Нет смен админов в календаре за этот период</TableCell></TableRow>
                            )}
                            {admins.map((r) => (
                                <TableRow
                                    key={r.employee_id}
                                    hover
                                    onClick={() => openDetail(r.employee_id, 'admin')}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>{r.employee_name}</TableCell>
                                    <TableCell align="right">{r.shifts}</TableCell>
                                    <TableCell align="right">{r.bar_shifts || 0}</TableCell>
                                    <TableCell align="right">{r.main_shifts ?? ((r.shifts || 0) - (r.bar_shifts || 0))}</TableCell>
                                    <TableCell align="right">{money(r.souvenirs_pay)}</TableCell>
                                    <TableCell align="right"><b>{money(r.total)}</b></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                        {admins.length > 0 && (
                            <TableFooter>
                                <TableRow>
                                    <TableCell><b>Итого</b></TableCell>
                                    <TableCell align="right"><b>{admins.reduce((s, r) => s + r.shifts, 0)}</b></TableCell>
                                    <TableCell align="right"><b>{admins.reduce((s, r) => s + (r.bar_shifts || 0), 0)}</b></TableCell>
                                    <TableCell align="right"><b>{admins.reduce((s, r) => s + (r.main_shifts ?? ((r.shifts || 0) - (r.bar_shifts || 0))), 0)}</b></TableCell>
                                    <TableCell align="right"><b>{money(admins.reduce((s, r) => s + r.souvenirs_pay, 0))}</b></TableCell>
                                    <TableCell align="right"><b>{money(data.totals.admins)}</b></TableCell>
                                </TableRow>
                            </TableFooter>
                        )}
                    </Table>
                </TableContainer>
            )}

            <Dialog open={!!detail} onClose={() => setDetail(null)} fullWidth maxWidth="md">
                <DialogTitle>
                    {detailLoading || detail?.loading
                        ? 'Расшифровка…'
                        : `${detail?.employee?.full_name || ''} · ${fmtDay(detail?.period_start)} – ${fmtDay(detail?.period_end)}`}
                </DialogTitle>
                <DialogContent>
                    {(detailLoading || detail?.loading) ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                    ) : detail?.kind === 'guide' ? (
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <Paper variant="outlined" sx={{ p: 2, flex: 1, bgcolor: '#f8fafc' }}>
                                    <Typography variant="body2" color="text.secondary">Фикс · выплачивается отдельно</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                                        {money(detail.totals?.shift_pay)}
                                    </Typography>
                                    {(detail.sessions || []).filter((s) => s.paid_shift && s.shift_pay).length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">Нет факта и замен</Typography>
                                    ) : (
                                        (detail.sessions || []).filter((s) => s.paid_shift && s.shift_pay).map((s, i) => (
                                            <Stack key={`f-${s.date}-${s.code}-${i}`} direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.25 }}>
                                                <Typography variant="body2">{fmtDay(s.date)} · {s.type}</Typography>
                                                <Typography variant="body2">{money(s.shift_pay)}</Typography>
                                            </Stack>
                                        ))
                                    )}
                                </Paper>
                                <Paper variant="outlined" sx={{ p: 2, flex: 1, bgcolor: '#f8fafc' }}>
                                    <Typography variant="body2" color="text.secondary">Допы</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                                        {money(detail.totals?.extras_pay ?? detail.totals?.total)}
                                    </Typography>
                                    {(detail.sessions || []).flatMap((s, i) => {
                                        const items = (s.bonus_items && s.bonus_items.length)
                                            ? s.bonus_items
                                            : (s.bonuses ? [{ label: s.type, amount: s.bonuses }] : []);
                                        return items.filter((item) => item.amount).map((item, j) => (
                                            <Stack key={`e-${s.date}-${i}-${j}`} direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.25 }}>
                                                <Typography variant="body2">{fmtDay(s.date)} · {item.label}</Typography>
                                                <Typography variant="body2">{money(item.amount)}</Typography>
                                            </Stack>
                                        ));
                                    })}
                                    {detail.totals?.cafe_pay ? (
                                        <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.25 }}>
                                            <Typography variant="body2">
                                                Кафе {money(detail.totals.cafe_revenue)} × {detail.rates?.cafe_percent}%
                                            </Typography>
                                            <Typography variant="body2">{money(detail.totals.cafe_pay)}</Typography>
                                        </Stack>
                                    ) : null}
                                </Paper>
                            </Stack>

                            <Typography variant="subtitle2">Дни</Typography>
                            <Stack divider={<Divider />} >
                                {(detail.sessions || []).map((s, i) => (
                                    <Box key={`d-${s.date}-${s.code}-${i}`} sx={{ py: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{dayTitle(s)}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {[
                                                s.paid_shift && s.shift_pay ? `фикс ${money(s.shift_pay)}` : null,
                                                s.bonuses ? `допы ${money(s.bonuses)}` : null,
                                                !(s.paid_shift && s.shift_pay) && !s.bonuses ? 'без оплаты в этом расчёте' : null,
                                            ].filter(Boolean).join(' · ')}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>

                            {(detail.cafe || []).length > 0 && (
                                <>
                                    <Typography variant="subtitle2">Кафе по дням</Typography>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Название</TableCell>
                                                    <TableCell>Что</TableCell>
                                                    <TableCell align="right">Кол-во</TableCell>
                                                    <TableCell align="right">Сумма</TableCell>
                                                    <TableCell align="right">{detail.rates?.cafe_percent}%</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {groupByDate(detail.cafe).flatMap((group) => {
                                                    const daySum = group.items.reduce((s, c) => s + (Number(c.amount) || 0), 0);
                                                    const dayPay = Math.round(daySum * (detail.rates?.cafe_percent || 0)) / 100;
                                                    return [
                                                        <TableRow key={`h-${group.date}`} sx={{ bgcolor: '#eef2ff' }}>
                                                            <TableCell colSpan={5} sx={{ py: 1 }}>
                                                                <Typography variant="subtitle2">{fmtDayTitle(group.date)}</Typography>
                                                            </TableCell>
                                                        </TableRow>,
                                                        ...group.items.map((c, i) => (
                                                            <TableRow key={`${group.date}-${c.name}-${i}`}>
                                                                <TableCell sx={{ pl: 3 }}>{c.name}</TableCell>
                                                                <TableCell>{c.source}</TableCell>
                                                                <TableCell align="right">{c.quantity}</TableCell>
                                                                <TableCell align="right">{money(c.amount)}</TableCell>
                                                                <TableCell align="right">{money(Math.round(c.amount * (detail.rates?.cafe_percent || 0)) / 100)}</TableCell>
                                                            </TableRow>
                                                        )),
                                                        <TableRow key={`t-${group.date}`}>
                                                            <TableCell colSpan={3} align="right" sx={{ borderBottom: '2px solid #c7d2fe' }}>
                                                                <b>Итого за день</b>
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ borderBottom: '2px solid #c7d2fe' }}><b>{money(daySum)}</b></TableCell>
                                                            <TableCell align="right" sx={{ borderBottom: '2px solid #c7d2fe' }}><b>{money(dayPay)}</b></TableCell>
                                                        </TableRow>,
                                                    ];
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}
                        </Stack>
                    ) : detail?.kind === 'admin' ? (
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc' }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Как считается</Typography>
                                {(detail.formula || []).map((line) => (
                                    <Typography key={line} variant="body2">{line}</Typography>
                                ))}
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="h6">Итого {money(detail.totals?.total)}</Typography>
                            </Paper>
                            <Typography variant="subtitle2">Смены</Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Дата</TableCell>
                                            <TableCell>Тип</TableCell>
                                            <TableCell align="right">Оплата</TableCell>
                                            <TableCell>Комментарий</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(detail.shifts || []).map((s) => (
                                            <TableRow key={s.date + s.kind}>
                                                <TableCell>{fmtDay(s.date)}</TableCell>
                                                <TableCell>{s.kind}</TableCell>
                                                <TableCell align="right">{money(s.pay)}</TableCell>
                                                <TableCell>{s.notes || s.why}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Typography variant="subtitle2">Сувениры ({detail.rates?.souvenirs_percent}%)</Typography>
                            {(detail.souvenirs || []).length === 0 ? (
                                <Typography variant="body2" color="text.secondary">Продаж сувениров за период нет</Typography>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Дата</TableCell>
                                                <TableCell align="right">Выручка</TableCell>
                                                <TableCell align="right">{detail.rates?.souvenirs_percent}%</TableCell>
                                                <TableCell>Комментарий</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {detail.souvenirs.map((s, i) => (
                                                <TableRow key={`${s.date}-${i}`}>
                                                    <TableCell>{fmtDay(s.date)}</TableCell>
                                                    <TableCell align="right">{money(s.amount)}</TableCell>
                                                    <TableCell align="right">{money(s.pay)}</TableCell>
                                                    <TableCell>{s.notes || '—'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetail(null)}>Закрыть</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
